// src/pages/CompletePayPage.jsx
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { initMercadoPago, Payment } from "@mercadopago/sdk-react";
import { getReservation, completePaymentCard } from "../api/pay";
import CardResumenReservaPay from "../components/CardResumenReservaPay";

const CompletePayPage = () => {
  const [reservationData, setReservationData] = useState(null);
  const [reservationID, setReservationID] = useState(null);
  const [initialization, setInitialization] = useState(null);
  const [paymentOption, setPaymentOption] = useState("50");
  const [showOption, setShowOption] = useState(false);
  const [amountToPay, setAmountToPay] = useState(0);
  const [paymentKey, setPaymentKey] = useState(0);
  const [showError, setShowError] = useState(false);
  const [showBrick, setShowBrick] = useState(true);
  const [sandboxInitPoint, setSandboxInitPoint] = useState(null);
  const [prodInitPoint, setProdInitPoint] = useState(null);
  const [preferenceId, setPreferenceId] = useState(null);

  // 🔑 MP dinámico
  const [mpPublicKey, setMpPublicKey] = useState(null);
  const [mpLocale, setMpLocale] = useState("es-AR");

  // 💱 Currency dinámica (ARS / CLP, etc.)
  const [currency, setCurrency] = useState("ARS");

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getReservationIdFromHash = () => {
    const hash = window.location.hash;
    const queryString = hash.includes("?") ? hash.split("?")[1] : "";
    const params = new URLSearchParams(queryString);
    return params.get("reservationId");
  };

  const calcularYActualizarBrick = async (reservation, pagarTodo = false) => {
    const seña = Number(reservation.seña || 0);
    const total = Number(reservation.total || 0);

    let montoFinal = pagarTodo ? total : Math.round(total * 0.5);

    // Si ya abonó una seña, se descuenta
    if (seña > 0 && seña < montoFinal) {
      montoFinal -= seña;
    }

    if (montoFinal <= 0) {
      setInitialization(null);
      setShowError(true);
      return;
    }

    try {
      setShowBrick(false);

      const res = await getReservation({
        reservationId: reservation.reservationId,
        amount: montoFinal,
      });

      setAmountToPay(montoFinal);
      setInitialization({
        amount: montoFinal,
        preferenceId: res.data.preferenceId,
      });

      // 🔑 Por si en este segundo getReservation también vienen updates de key/locale/currency
      if (res.data.mpPublicKey) setMpPublicKey(res.data.mpPublicKey);
      if (res.data.mpLocale) setMpLocale(res.data.mpLocale);
      if (res.data.currency)
        setCurrency(String(res.data.currency || "ARS").toUpperCase());

      setTimeout(() => {
        setShowBrick(true);
        setPaymentKey((prev) => prev + 1);
      }, 100);
    } catch (err) {
      console.error("❌ Error al actualizar preferencia:", err);
    }
  };

  // 🧠 MP: inicializar cuando tengamos la public key del backend
  useEffect(() => {
    if (!mpPublicKey) {
      console.warn("⚠️ No hay mpPublicKey aún para initMercadoPago");
      return;
    }

    initMercadoPago(mpPublicKey, {
      locale: mpLocale || "es-AR",
    });

    console.log("✅ initMercadoPago en CompletePayPage con:", {
      mpPublicKey,
      mpLocale,
    });
  }, [mpPublicKey, mpLocale]);

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const reservationIdFromUrl = getReservationIdFromHash();
    if (!reservationIdFromUrl || reservationID) return;

    const fetchData = async () => {
      try {
        const res = await getReservation({
          reservationId: reservationIdFromUrl,
        });

        const reservas = Array.isArray(res.data?.reservations)
          ? res.data.reservations
          : [res.data.reservation];

        setReservationID(reservationIdFromUrl);

        const guests = reservas.reduce(
          (acc, r) => acc + Number(r.guests || 1),
          0
        );
        const total = reservas.reduce(
          (acc, r) => acc + Number(r.amount || 0),
          0
        );
        const seña = reservas.reduce((acc, r) => acc + Number(r.seña || 0), 0);
        const pricePerNight = guests > 0 ? Math.round(total / guests) : 0;

        const datosReserva = {
          ...reservas[0],
          pricePerNight,
          total,
          amount: total,
          numGuests: guests,
          seña,
        };

        setShowOption(reservas.every((r) => Number(r.seña) === 0));
        setSandboxInitPoint(res.data.sandbox_init_point);
        setProdInitPoint(res.data.init_point);
        setPreferenceId(res.data.preferenceId);
        setReservationData(datosReserva);

        // 🔑 Setear clave / locale / currency que vienen del backend
        if (res.data.mpPublicKey) setMpPublicKey(res.data.mpPublicKey);
        if (res.data.mpLocale) setMpLocale(res.data.mpLocale);
        if (res.data.currency) {
          setCurrency(String(res.data.currency || "ARS").toUpperCase());
        } else if (reservas[0]?.currency) {
          setCurrency(String(reservas[0].currency || "ARS").toUpperCase());
        }

        await calcularYActualizarBrick(datosReserva, false);
      } catch (err) {
        console.error("❌ Error cargando la reserva:", err);
        setShowError(true);
      }
    };

    fetchData();
  }, [reservationID]);

  const handleOptionChange = async (event) => {
    const payFull = event.target.checked;
    setPaymentOption(payFull ? "100" : "50");

    if (reservationData) {
      await calcularYActualizarBrick(
        {
          ...reservationData,
          amount: reservationData.total,
        },
        payFull
      );
    }
  };

  const onSubmit = async ({ formData: mpData }) => {
    if (!reservationID) return;
    try {
      await completePaymentCard({
        formData: mpData,
        reservationId: reservationID,
        amount: amountToPay,
        payOption: paymentOption,
      });
      navigate("/success");
    } catch (err) {
      console.error("❌ Error al procesar el pago:", err);
    }
  };

  if (!reservationData || !initialization) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6">Cargando datos de la reserva...</Typography>
      </Box>
    );
  }

  if (showError) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          Ya abonaste el total de la reserva o no hay monto válido para pagar.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        backgroundColor: "#f3efe5",
        minHeight: "100vh",
        padding: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 4,
          width: "100%",
          maxWidth: 1200,
        }}
      >
        <Box
          sx={{
            width: isMobile ? "100%" : 360,
            position: isMobile ? "static" : "sticky",
            top: 100,
            alignSelf: "flex-start",
          }}
        >
          <CardResumenReservaPay
            amountToPay={amountToPay}
            currency={currency} // 💱 ARS / CLP dinámico
            {...reservationData}
          />
        </Box>

        <Box
          sx={{
            backgroundColor: "#fff",
            padding: 4,
            borderRadius: 3,
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)",
            flex: 1,
          }}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{ fontWeight: "bold", textAlign: "center", mb: 3 }}
          >
            Completar Pago de la Reserva
          </Typography>

          {showOption && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={paymentOption === "100"}
                  onChange={handleOptionChange}
                  color="primary"
                />
              }
              label="Quiero pagar el total ahora"
              sx={{ mb: 2 }}
            />
          )}

          <Divider sx={{ my: 3 }}>Pagar con Tarjeta</Divider>

          {showBrick && (
            <Payment
              key={paymentKey}
              initialization={initialization}
              customization={{
                paymentMethods: { creditCard: "all", debitCard: "all" },
              }}
              onSubmit={onSubmit}
              onReady={() => console.log("✅ Brick listo")}
              onError={(err) => console.error("❌ Brick error:", err)}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CompletePayPage;
